import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Required, ValuesType } from 'utility-types';
import { ZodType, z } from 'zod';
import * as httpStatusCodes from './api.statusCodes';

export const baseResponseSchema = z.object({
  status: z.string().optional(),
  channel: z.number().optional(),
  requestId: z.string().optional(),
  datetime: z.string().optional(),
});

export const baseErrorResponseSchema = baseResponseSchema.extend({
  error: z.object({
    type: z.string(),
    message: z.string().optional(),
    errorInfos: z.record(z.string(), z.array(z.string())).optional(),
  }),
});

export type RequestPayload = {
  requestConfig: AxiosRequestConfig;
};

export type RequestConfig = Required<AxiosRequestConfig, 'url' | 'method'> & {
  allowSsoUrlRewrite?: boolean;
  enrichRequestData?: boolean;
};

export type BaseRequestApiDataPayload = {
  // axios config without 'url' option.
  // see: https://github.com/axios/axios/blob/master/README.md#request-config
  requestConfig: RequestConfig;
  // Uniquely identifies resources.
  resourceId: string;
  skipDefaultServerErrorHandling?: boolean;
  invalidateIfPresent?: boolean;
  successSchemaValidator?: ZodType;
  persistResponseDataInsideApiReducer?: boolean;
};

export type RequestApiDataIfNecessaryPayload = BaseRequestApiDataPayload;

export type ApiRequestInfo = {
  url: string;
  resourceId: string;
  timestamp: string;
  data?: AxiosResponse;
};

export type ApiRequestSuccessInfo = ApiRequestInfo & {
  didInvalidate: boolean;
  response: Pick<AxiosResponse<any>, 'headers' | 'status' | 'data'>;
  request: Pick<
    AxiosRequestConfig,
    'headers' | 'method' | 'url' | 'params' | 'data'
  >;
};

export type ApiErrorInfos = Record<string, string[]>;

export type ApiResponseErrorPayload = {
  datetime: string;
  type: string;
  error?: { errorInfos?: ApiErrorInfos };
};

export type ErrorResponse = Pick<
  AxiosResponse<any>,
  'headers' | 'status' | 'data'
>;

export type ApiRequestFailedInfo = ApiRequestInfo & {
  response?: ErrorResponse;
  errorMessage?: string;
  skipDefaultServerErrorHandling?: boolean;
  request: Pick<
    AxiosRequestConfig,
    'headers' | 'method' | 'url' | 'params' | 'data'
  >;
};

// https://github.com/axios/axios/blob/master/README.md#response-schema
// https://github.com/axios/axios/blob/master/index.d.ts#L73
export type RequestApiDataSuccessPayload<T = any> =
  BaseRequestApiDataPayload & { response: AxiosResponse<T> };

// https://github.com/axios/axios/blob/master/index.d.ts#L82
export type RequestApiDataFailedPayload<T = any> = BaseRequestApiDataPayload & {
  error?: AxiosError<T>;
};

export interface RequestLoadingState {
  isLoaded: boolean;
  hasFailed: boolean;
  isInvalidated: boolean;
}

export interface CompoundLoadState {
  hasLoadedAll: boolean;
  hasFailedToLoad: boolean;
  hasInvalidatedData: boolean;
}

export type HttpStatusCodes = ValuesType<typeof httpStatusCodes>;

export type HttpStatusCodesKeys = keyof typeof httpStatusCodes;

export interface ApiResponseBasePayload {
  datetime: string;
  status: string;
  requestId: string;
}
